import React, { useState, useEffect, useCallback } from 'react';
import Swal from '../../theme/swal';
import SectionCard from '../../components/SectionCard';
import { CardSkeleton } from '../../components/Skeleton';
import { IcPlus, IcPencil, IcClose, IcSave, IcMapPin, IcAlertTriangle, IcArrowUp, IcArrowDown } from '../../theme/icons';
import authService from '../../services/authService';
import sellerIdentityService from '../../services/sellerIdentityService';
import storeService from '../../services/storeService';
import KI from '../../theme/palette';

// Paramètres › Boutiques (compte propriétaire) — refonte owner 2026-09-30.
// La page ne fait qu'une chose : dire qui travaille dans quelle boutique.
//   Caisse          = les comptes caisse (le compte ouvert en boutique)
//   Chefs d'équipe  = les PERSONNES de niveau manager : leur code
//                     ouvre les actions réservées dans LEUR boutique
//   Vendeurs        = les personnes vendeur (limites de la caisse)
// Vendeur ⇄ chef d'équipe se change ici, en un clic (devenir chef = donner un
// code, obligatoire). Un COMPTE manager peut aussi être rangé chef d'équipe
// d'une ou plusieurs boutiques (owner 2026-10-03) ; sans boutique il reste dans
// « Partout ». Dans tous les cas le code d'un compte manager / propriétaire
// marche sur toutes les caisses. ✕ retire de la boutique (la fiche reste dans
// Comptes / Personnes). Rien ne se crée ni ne se désactive ici.
// Dessin SIMPLE (owner 2026-09-30 : la refonte à tuiles / panneaux a été
// refusée) : une carte, un bloc par boutique, trois listes nues.
const inputClass = 'inp w-full pl-11 pr-3 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

// Une ligne : nom, détail, actions (changer de rôle, retirer).
const Row = ({ name, sub, active, switchLabel, SwitchIcon, onSwitch, onRemove, removeLabel, busy }) => (
  <li className="flex items-center gap-1 rounded-lg px-2 -ml-2 py-1 hover:bg-gray-100 transition-colors">
    <div className="flex-1 min-w-0">
      <p className={`flex items-center gap-2 text-sm leading-tight ${active ? 'text-gray-900' : 'text-gray-400'}`}>
        <span className="truncate">{name}</span>
        {!active && <span className="bdg bdg-neutral">Désactivé</span>}
      </p>
      {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
    </div>
    {onSwitch && (
      <button type="button" onClick={onSwitch} disabled={busy} title={switchLabel} aria-label={`${switchLabel} : ${name}`}
        className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-200 transition-colors disabled:opacity-40">
        <SwitchIcon />
      </button>
    )}
    <button type="button" onClick={onRemove} disabled={busy} title={removeLabel} aria-label={removeLabel}
      className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40">
      <IcClose />
    </button>
  </li>
);

// Une colonne : titre, lignes, puis « Choisir… » pour en prendre un(e) qui existe.
const Panel = ({ title, empty, placeholder, candidates, onChoose, label, children }) => (
  <div className="min-w-0 flex flex-col">
    <p className="text-[11px] tracking-[0.8px] uppercase text-gray-400 mb-2">{title}</p>
    {React.Children.count(children) > 0
      ? <ul className="space-y-0.5">{children}</ul>
      : <p className="text-sm text-gray-400 py-1.5">{empty}</p>}
    <select value="" aria-label={label} disabled={candidates.length === 0}
      onChange={(e) => { const c = candidates.find((x) => x.key === e.target.value); if (c) onChoose(c); }}
      className="inp w-full mt-2 px-3 py-2 rounded-xl text-sm text-gray-700 disabled:opacity-60">
      <option value="">{candidates.length ? placeholder : 'Personne d’autre à choisir'}</option>
      {candidates.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
    </select>
  </div>
);

const BoutiquesList = () => {
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edit, setEdit] = useState(null);          // { id?, name } | null — boutique
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);          // clé de la ligne en cours

  const load = useCallback(async () => {
    try {
      const [s, u, p] = await Promise.all([
        storeService.list(), authService.getAllUsers(), sellerIdentityService.listPeople(),
      ]);
      setStores(s);
      setUsers(Array.isArray(u) ? u : (u?.results || []));
      setPeople(Array.isArray(p) ? p : []);
      setError(null);
    } catch (_) {
      setError('Impossible de charger les boutiques.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setEditError('');
    if (!edit.name.trim()) { setEditError('Le nom est requis.'); return; }
    try {
      setSaving(true);
      await storeService.save({ id: edit.id, name: edit.name.trim() });
      setEdit(null);
      load();
    } catch (err) {
      setEditError((err && (err.name?.[0] || err.detail)) || "Impossible d'enregistrer la boutique.");
    } finally {
      setSaving(false);
    }
  };

  // ---- qui est où
  const idsOf = (x) => x.stores || [];
  const nameOf = (id) => (stores.find((st) => st.id === id) || {}).name || '';
  const namesOf = (ids) => ids.map(nameOf).filter(Boolean).join(', ');
  const signs = (x) => (x.has_pin ? 'code' : 'sans code');
  const alsoIn = (x, store) => {
    const other = namesOf(idsOf(x).filter((i) => i !== store.id));
    return other ? ` · aussi ${other}` : '';
  };
  const caisseAccounts = users.filter((u) => u.role === 'seller');
  const managerAccounts = users.filter((u) => u.role === 'manager');
  const chefs = people.filter((p) => p.level === 'manager');
  const vendeurs = people.filter((p) => p.level === 'seller');
  // Les 3 familles : caisse / vendeur = une boutique ; chef d'équipe = plusieurs
  // possibles. Chef d'équipe = une PERSONNE manager, ou un COMPTE manager
  // (owner 2026-10-03 : « parfois un manager doit être chef d'équipe »).
  const KINDS = {
    caisse: { entries: () => caisseAccounts.map((x) => ({ x, type: 'account', nameKey: 'username' })), multi: false, word: 'une caisse' },
    chef: {
      entries: () => [
        ...chefs.map((x) => ({ x, type: 'person', nameKey: 'name' })),
        ...managerAccounts.map((x) => ({ x, type: 'account', nameKey: 'username', tag: 'compte manager' })),
      ],
      multi: true, word: 'un chef d’équipe',
    },
    vendeur: { entries: () => vendeurs.map((x) => ({ x, type: 'person', nameKey: 'name' })), multi: false, word: 'un vendeur' },
  };
  const fail = (err, fallback) => Swal.fire('Erreur', (err && (err.detail || err.stores?.[0])) || fallback, 'error');

  const saveStores = (type, id, next) => (type === 'account'
    ? authService.updateUserStatus(id, { stores: next })
    : sellerIdentityService.savePerson({ id, stores: next }));

  const candidatesFor = (store, kind) => {
    const k = KINDS[kind];
    return k.entries().filter(({ x }) => !idsOf(x).includes(store.id)).map(({ x, type, nameKey, tag }) => {
      const ids = idsOf(x);
      const where = ids.length ? namesOf(ids) : (tag ? 'partout' : 'sans boutique');
      return { key: `${type}${x.id}`, type, id: x.id, name: x[nameKey], ids, hasPin: !!x.has_pin, tag,
        label: `${x[nameKey]}${tag ? ` (${tag})` : ''} — ${where}${x.is_active ? '' : ' (désactivé)'}` };
    }).sort((a, b) => a.name.localeCompare(b.name));
  };

  const choose = async (store, kind, c) => {
    const k = KINDS[kind];
    const next = k.multi ? [...c.ids, store.id] : [store.id];
    if (!k.multi && c.ids.length) {   // caisse / vendeur : une seule boutique
      const res = await Swal.fire({
        title: `Passer ${c.name} dans ${store.name} ?`,
        text: `${c.name} quittera ${namesOf(c.ids)} : ${k.word} ne travaille que dans une seule boutique.`,
        icon: 'question', showCancelButton: true, confirmButtonColor: KI.ink,
        confirmButtonText: 'Oui, passer', cancelButtonText: 'Annuler', reverseButtons: true,
      });
      if (!res.isConfirmed) return;
    }
    // Devenir chef d'équipe = avoir un code (owner 2026-10-03). Un compte
    // manager sans code à la caisse en reçoit un ici (2 à 12).
    let pin = null;
    if (kind === 'chef' && c.type === 'account' && !c.hasPin) {
      const ask = await Swal.fire({
        title: `Code à la caisse pour ${c.name}`,
        text: 'Un chef d’équipe ouvre les actions réservées : ce compte manager n’a pas encore de code. Donnez-le (2 à 12 lettres ou chiffres, tapé au clavier).',
        input: 'text', inputAttributes: { maxlength: 12, autocapitalize: 'off', autocomplete: 'off' },
        inputValidator: (v) => (/^[a-zA-Z0-9]{2,12}$/.test(v || '') ? null : '2 à 12 lettres ou chiffres, sans espace.'),
        showCancelButton: true, confirmButtonColor: KI.ink, confirmButtonText: 'Passer chef d’équipe', cancelButtonText: 'Annuler', reverseButtons: true,
      });
      if (!ask.isConfirmed) return;
      pin = ask.value;
    }
    setBusy(c.key);
    try {
      await (pin !== null ? authService.updateUserStatus(c.id, { stores: next, pin }) : saveStores(c.type, c.id, next));
      await load();
    } catch (err) { fail(err, `Impossible d'ajouter ${c.name} à ${store.name}.`); } finally { setBusy(null); }
  };

  const remove = async (store, x, type, name) => {
    const rest = idsOf(x).filter((i) => i !== store.id);
    const res = await Swal.fire({
      title: `Retirer ${name} de ${store.name} ?`,
      text: rest.length
        ? `${name} reste dans ${namesOf(rest)}.`
        : `${name} reste dans ${type === 'account' ? 'Comptes' : 'Personnes'}, sans boutique : plus reconnu(e) en caisse tant qu’il / elle n’est pas rangé(e) dans une boutique.`,
      icon: 'question', showCancelButton: true, confirmButtonColor: KI.ink,
      confirmButtonText: 'Retirer', cancelButtonText: 'Annuler', reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    setBusy(`${type}${x.id}`);
    try { await saveStores(type, x.id, rest); await load(); } catch (err) { fail(err, `Impossible de retirer ${name}.`); } finally { setBusy(null); }
  };

  // Vendeur → chef d'équipe : son code approuvera des actions réservées, on en
  // demande TOUJOURS un nouveau (owner 2026-10-03 : code obligatoire en devenant
  // chef ; 2 à 12 caractères).
  const promote = async (p) => {
    const ask = await Swal.fire({
      title: `Nouveau code pour ${p.name}`,
      text: 'Un chef d’équipe ouvre les actions réservées : donnez-lui son code (2 à 12 lettres ou chiffres, tapé au clavier).',
      input: 'text', inputAttributes: { maxlength: 12, autocapitalize: 'off', autocomplete: 'off' },
      inputValidator: (v) => (/^[a-zA-Z0-9]{2,12}$/.test(v || '') ? null : '2 à 12 lettres ou chiffres, sans espace.'),
      showCancelButton: true, confirmButtonColor: KI.ink, confirmButtonText: 'Passer chef d’équipe', cancelButtonText: 'Annuler', reverseButtons: true,
    });
    if (!ask.isConfirmed) return;
    setBusy(`person${p.id}`);
    try { await sellerIdentityService.savePerson({ id: p.id, level: 'manager', pin: ask.value }); await load(); }
    catch (err) { fail(err, `Impossible de passer ${p.name} chef d’équipe.`); } finally { setBusy(null); }
  };

  // Chef d'équipe → vendeur (dans cette boutique : un vendeur n'en a qu'une).
  const demote = async (store, p) => {
    const others = namesOf(idsOf(p).filter((i) => i !== store.id));
    if (others) {
      const res = await Swal.fire({
        title: `Repasser ${p.name} vendeur ?`,
        text: `${p.name} restera vendeur de ${store.name} et quittera ${others} (un vendeur ne travaille que dans une boutique).`,
        icon: 'question', showCancelButton: true, confirmButtonColor: KI.ink,
        confirmButtonText: 'Oui', cancelButtonText: 'Annuler', reverseButtons: true,
      });
      if (!res.isConfirmed) return;
    }
    setBusy(`person${p.id}`);
    try { await sellerIdentityService.savePerson({ id: p.id, level: 'seller', stores: [store.id] }); await load(); }
    catch (err) { fail(err, `Impossible de repasser ${p.name} vendeur.`); } finally { setBusy(null); }
  };

  const card = (store) => {
    const here = (x) => idsOf(x).includes(store.id);
    const byActive = (a, b) => Number(b.is_active) - Number(a.is_active);
    const caisses = caisseAccounts.filter(here).sort(byActive);
    const myChefs = chefs.filter(here).sort(byActive);
    const myManagerAccounts = managerAccounts.filter(here).sort(byActive);
    const mySellers = vendeurs.filter(here).sort(byActive);
    const missing = [];
    if (!caisses.some((u) => u.is_active)) missing.push('Pas de caisse');
    if (![...myChefs, ...mySellers].some((p) => p.is_active)) missing.push('Personne pour vendre');
    const panel = (kind) => ({ candidates: candidatesFor(store, kind), onChoose: (c) => choose(store, kind, c) });
    const common = (x, type, name) => ({
      name, active: x.is_active, busy: busy === `${type}${x.id}`,
      removeLabel: `Retirer ${name} de ${store.name}`, onRemove: () => remove(store, x, type, name),
    });

    return (
      <div key={store.id} className="px-6 py-5 border-t border-gray-100 first:border-t-0">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="text-base font-semibold text-gray-900 mr-1">{store.name}</h3>
          {missing.map((m) => <span key={m} className="bdg bdg-warn"><IcAlertTriangle size={12} /> {m}</span>)}
          <button type="button" onClick={() => { setEdit({ id: store.id, name: store.name }); setEditError(''); }}
            className="btn-g-edit gap-2 px-3 py-1.5 text-sm transition-colors ml-auto">
            <IcPencil /> Renommer
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <Panel title="Caisse" empty="Aucune caisse"
            placeholder="Choisir une caisse…" label={`Choisir une caisse pour ${store.name}`} {...panel('caisse')}>
            {caisses.map((u) => <Row key={`u${u.id}`} sub={u.email} {...common(u, 'account', u.username)} />)}
          </Panel>
          <Panel title="Chefs d’équipe" empty="Aucun chef d’équipe"
            placeholder="Choisir un chef d’équipe…" label={`Choisir un chef d’équipe pour ${store.name}`} {...panel('chef')}>
            {myChefs.map((p) => (
              <Row key={`p${p.id}`} sub={`${signs(p)}${alsoIn(p, store)}`} {...common(p, 'person', p.name)}
                switchLabel="Repasser vendeur" SwitchIcon={IcArrowDown} onSwitch={() => demote(store, p)} />
            ))}
            {myManagerAccounts.map((u) => (
              <Row key={`u${u.id}`} sub={`Compte manager · ${signs(u)}${alsoIn(u, store)}`} {...common(u, 'account', u.username)} />
            ))}
          </Panel>
          <Panel title="Vendeurs" empty="Aucun vendeur"
            placeholder="Choisir un vendeur…" label={`Choisir un vendeur pour ${store.name}`} {...panel('vendeur')}>
            {mySellers.map((p) => (
              <Row key={`p${p.id}`} sub={signs(p)} {...common(p, 'person', p.name)}
                switchLabel="Passer chef d’équipe" SwitchIcon={IcArrowUp} onSwitch={() => promote(p)} />
            ))}
          </Panel>
        </div>
      </div>
    );
  };

  // Partout : comptes propriétaire, comptes manager SANS boutique (rangé chef
  // d'équipe, un compte manager apparaît dans ses boutiques) et les personnes
  // de niveau propriétaire. Le code d'un compte marche partout dans tous les cas.
  const everywhere = [
    ...users.filter((u) => u.role !== 'seller' && u.is_active && (u.role === 'admin' || idsOf(u).length === 0)).map((u) => ({
      key: `u${u.id}`, name: u.username, sub: u.email, role: u.role === 'admin' ? 'Propriétaire' : 'Manager',
      badge: u.role === 'admin' ? 'bdg-warn' : 'bdg-pos', has_pin: u.has_pin })),
    ...people.filter((p) => p.level === 'owner' && p.is_active).map((p) => ({
      key: `p${p.id}`, name: p.name, sub: 'Personne', role: 'Propriétaire', badge: 'bdg-warn', has_pin: p.has_pin })),
  ];
  const loose = [
    ...caisseAccounts.filter((u) => idsOf(u).length === 0).map((u) => u.username),   // les comptes manager sans boutique sont « Partout », pas perdus
    ...[...chefs, ...vendeurs].filter((p) => idsOf(p).length === 0).map((p) => p.name),
  ];
  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4" role="alert">{error}</div>}

      <SectionCard
        title="Boutiques"
        sub="Qui travaille dans chaque boutique. Les fiches se modifient dans Comptes et Personnes."
        flush
        action={!loading && (
          <button
            type="button"
            onClick={() => { setEdit({ name: '' }); setEditError(''); }}
            className="h-9 w-9 flex items-center justify-center btn-mini transition-colors shrink-0"
            title="Ajouter une boutique"
            aria-label="Ajouter une boutique"
          >
            <IcPlus />
          </button>
        )}
      >
        {loading ? <div className="p-6"><CardSkeleton /></div> : (
          <>
            {stores.map(card)}
            {loose.length > 0 && (
              <p className="px-6 py-3 border-t border-gray-100 text-sm text-amber-800 bg-amber-50">
                <span className="font-medium">Sans boutique :</span> {loose.join(', ')}
              </p>
            )}
            <p className="px-6 py-3 border-t border-gray-100 text-sm text-gray-600">
              <span className="text-[11px] tracking-[0.8px] uppercase text-gray-400 mr-2">Partout</span>
              {everywhere.map((x) => `${x.name}${x.has_pin ? '' : ' (sans code)'}`).join(', ') || '—'}
              <span className="text-gray-400"> — comptes propriétaire et manager : leur code marche sur toutes les caisses.</span>
            </p>
          </>
        )}
      </SectionCard>

      {edit && (
        <div className="modal-backdrop z-50 flex items-start justify-center p-4 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) setEdit(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md my-8 overflow-hidden">
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">{edit.id ? 'Renommer la boutique' : 'Nouvelle boutique'}</h3>
              <button type="button" onClick={() => setEdit(null)} className="text-gray-400 hover:text-white" aria-label="Fermer"><IcClose /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              {editError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl" role="alert">{editError}</div>}
              <div>
                <label className={labelClass} htmlFor="store-name">Nom <span className="req">*</span></label>
                <div className="relative">
                  <IcMapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input id="store-name" className={inputClass} type="text" value={edit.name} autoFocus autoComplete="off"
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="ex : Ki Para 2" required />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-2">
                <button type="button" onClick={() => setEdit(null)} className="btn-g px-5 py-2.5 transition">Annuler</button>
                <button type="submit" disabled={saving} className={`${edit.id ? 'btn-p' : 'btn-add-pop'} gap-2 px-5 py-2.5 transition`}><IcSave /> {saving ? 'Enregistrement…' : (edit.id ? 'Enregistrer' : 'Ajouter la boutique')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoutiquesList;
